/**
 * Pure translation from a MediaPipe result into the boundary's own {@link PoseFrame}.
 *
 * Kept separate from the engine so the shape of what detectors receive can be tested in jsdom.
 * jsdom cannot run the WASM vision runtime at all, so anything that must be proved without a
 * browser has to live on this side of the line.
 */

import {
  CORE_POSE_LANDMARKS,
  POSE_LANDMARK_COUNT,
  type PoseFrame,
  type PoseLandmarkPoint,
  type PoseObservation,
} from './pose-types';

/**
 * A landmark as MediaPipe hands it over. `visibility` is optional here on purpose: world
 * landmarks omit it in some builds, and a missing value must read as "unknown, therefore not
 * confident", never as a confident 1.
 */
export interface RawPoseLandmark {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly visibility?: number;
}

/** The subset of `PoseLandmarkerResult` this module reads. */
export interface RawPoseResult {
  readonly landmarks: readonly (readonly RawPoseLandmark[])[];
  readonly worldLandmarks: readonly (readonly RawPoseLandmark[])[];
}

function clampUnit(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function copyPoint(point: RawPoseLandmark): PoseLandmarkPoint {
  return { x: point.x, y: point.y, z: point.z, visibility: clampUnit(point.visibility) };
}

/**
 * Confidence for one pose.
 *
 * MediaPipe's PoseLandmarker result carries no per-pose score — only per-landmark visibility — so
 * the boundary derives one. The mean over the landmarks the detectors actually use is the value
 * the Slice B adapter thresholds (D-059 PS3-1); `minCoreVisibility` travels beside it because a
 * single occluded ankle is invisible in a mean and matters to a crouch detector.
 */
export function coreVisibility(landmarks: readonly RawPoseLandmark[]): {
  coreConfidence: number;
  minCoreVisibility: number;
} {
  const values = CORE_POSE_LANDMARKS.map((index) => clampUnit(landmarks[index]?.visibility));
  if (values.length === 0) return { coreConfidence: 0, minCoreVisibility: 0 };

  const total = values.reduce((sum, value) => sum + value, 0);
  return { coreConfidence: total / values.length, minCoreVisibility: Math.min(...values) };
}

/**
 * Converts one pose. Returns `null` for a result that is not the 33-point topology the detectors
 * are written against — a mis-shaped pose is dropped rather than passed on as a partial body.
 */
export function toPoseObservation(
  poseIndex: number,
  landmarks: readonly RawPoseLandmark[],
  worldLandmarks: readonly RawPoseLandmark[],
): PoseObservation | null {
  if (landmarks.length !== POSE_LANDMARK_COUNT) return null;

  const { coreConfidence, minCoreVisibility } = coreVisibility(landmarks);

  return {
    poseIndex,
    landmarks: landmarks.map(copyPoint),
    worldLandmarks: worldLandmarks.map(copyPoint),
    coreConfidence,
    minCoreVisibility,
  };
}

/** Timing recorded around a single detection call. */
export interface PoseFrameTiming {
  readonly timestampMs: number;
  readonly detectedAt: number;
  readonly detectionLatencyMs: number;
}

/** Converts a whole detection result into the frame handed to detector code. */
export function toPoseFrame(result: RawPoseResult, timing: PoseFrameTiming): PoseFrame {
  const poses: PoseObservation[] = [];

  result.landmarks.forEach((landmarks, index) => {
    const observation = toPoseObservation(index, landmarks, result.worldLandmarks[index] ?? []);
    if (observation !== null) poses.push(observation);
  });

  return {
    timestampMs: timing.timestampMs,
    detectedAt: timing.detectedAt,
    detectionLatencyMs: timing.detectionLatencyMs,
    poses,
  };
}

/**
 * MediaPipe's VIDEO mode rejects a timestamp that does not strictly increase, and throws in a way
 * that kills the task rather than dropping the frame. Two frames sharing a millisecond is
 * ordinary at 60 fps, so the boundary nudges instead of failing: the child's session must not end
 * because two `requestVideoFrameCallback`s landed in the same tick.
 */
export function createMonotonicTimestamps(): (candidate: number) => number {
  let last = Number.NEGATIVE_INFINITY;

  return (candidate: number): number => {
    const safe = Number.isFinite(candidate) ? candidate : 0;
    const next = safe > last ? safe : last + 1;
    last = next;
    return next;
  };
}
